import OlMap from 'ol/Map';
import WKT from 'ol/format/WKT';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Stroke, Style } from 'ol/style';
import { SitePolygoneDto } from 'src/app/bo/site/sitePolygone';
import { DxfServiceService } from 'src/app/services/dxf-service.service';
import { MAP_PROJECTION, Z_INDEX } from '../site-map.constants';

const DEFAULT_EPSG = 4326;

/** Couches DXF d'un site (une couche vectorielle par fichier), que le panneau affiche ou masque individuellement. */
export class SitePolygonsLayer {
  private readonly layersByPolygoneId = new Map<number, VectorLayer<VectorSource>>();
  private readonly wkt = new WKT();
  private generation = 0;
  private epsg = DEFAULT_EPSG;

  constructor(private readonly map: OlMap, private readonly dxfService: DxfServiceService) {}

  /** Remplace les couches du site ; résout à `null` si un autre chargement a remplacé celui-ci entre-temps. */
  async load(siteId: number, epsg?: number): Promise<SitePolygoneDto[] | null> {
    const generation = ++this.generation;
    this.clear();
    // La couche est réutilisée d'un site à l'autre : sans EPSG, on ne garde pas la projection du site précédent.
    this.epsg = epsg ?? DEFAULT_EPSG;

    const polygones = (await this.dxfService.loadSitepolygones(siteId)) ?? [];
    if (generation !== this.generation) return null;
    polygones.forEach(polygone => this.addPolygone(polygone));
    return polygones;
  }

  setVisibility(polygoneId: number, visible: boolean): void {
    this.layersByPolygoneId.get(polygoneId)?.setVisible(visible);
  }

  dispose(): void {
    this.generation++;
    this.clear();
  }

  private clear(): void {
    this.layersByPolygoneId.forEach(layer => this.map.removeLayer(layer));
    this.layersByPolygoneId.clear();
  }

  private addPolygone(polygone: SitePolygoneDto): void {
    const layer = this.createLayer(polygone);
    if (!layer) return;
    layer.setZIndex(Z_INDEX.dxf);
    // Visible par défaut quand l'indicateur est absent (anciennes lignes).
    layer.setVisible(polygone.isVisible !== false);
    this.map.addLayer(layer);
    this.layersByPolygoneId.set(polygone.id, layer);
  }

  /** Format récent : DXF compressé ; repli sur l'ancien format WKT s'il est illisible ou vide. */
  private createLayer(polygone: SitePolygoneDto): VectorLayer<VectorSource> | null {
    if (polygone.compressedDxf) {
      try {
        // `parseDxfFile` renvoie null (et ne lève pas) sur un fichier illisible.
        const entities = this.dxfService.parseDxfFile(this.dxfService.decompressDxf(polygone.compressedDxf));
        if (entities?.length) return this.dxfService.createVectorLayer(entities, this.epsg);
      } catch {
        // Données compressées invalides : on retombe sur le WKT ci-dessous.
      }
    }
    return polygone.wktString ? this.createWktLayer(polygone) : null;
  }

  private createWktLayer(polygone: SitePolygoneDto): VectorLayer<VectorSource> {
    const style = new Style({ stroke: new Stroke({ color: polygone.color, width: 5 }) });
    const source = new VectorSource();
    polygone.wktString!.split('\n').forEach(element => {
      const feature = this.wkt.readFeature(element, { dataProjection: MAP_PROJECTION, featureProjection: MAP_PROJECTION });
      feature.setStyle(style);
      source.addFeature(feature);
    });
    return new VectorLayer({ source });
  }
}
